import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';
import { CalendarClock, CheckCircle2, MessageSquare, Search, Send, UserPlus, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
}).format(Number(value || 0));

const statusColor = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized.includes('converted') || normalized.includes('active')) return 'bg-emerald-100 text-emerald-700';
  if (normalized.includes('new') || normalized.includes('lead')) return 'bg-blue-100 text-blue-700';
  if (normalized.includes('lost') || normalized.includes('inactive')) return 'bg-red-100 text-red-700';
  return 'bg-amber-100 text-amber-700';
};

export default function CrmPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', entity_type: 'all', follow_up_due: false });
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [converting, setConverting] = useState(false);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = {
        search: filters.search || undefined,
        entity_type: filters.entity_type === 'all' ? undefined : filters.entity_type,
        follow_up_due: filters.follow_up_due || undefined,
        page_size: 100,
      };
      const response = await adminApi.getCrmRecords(params);
      setRecords(response.data?.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load CRM');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [filters.entity_type, filters.follow_up_due]);

  const stats = useMemo(() => {
    const leads = records.filter((item) => item.entity_type === 'lead').length;
    const customers = records.filter((item) => item.entity_type === 'customer').length;
    const due = records.filter((item) => item.follow_up_date && new Date(item.follow_up_date) <= new Date()).length;
    return { leads, customers, due };
  }, [records]);

  const openDetail = async (record) => {
    setSelected(record);
    setDetailLoading(true);
    setWhatsappMessage('');
    try {
      const response = await adminApi.getCrmRecord(record.entity_type, record.id);
      setSelected(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load CRM detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleConvertLead = async () => {
    if (!selected || selected.entity_type !== 'lead') return;
    setConverting(true);
    try {
      const response = await adminApi.convertLeadToCustomer(selected.id, {});
      toast.success('Lead converted to customer');
      setSelected({ ...selected, status: 'converted', converted_customer_id: response.data?.customer?.id });
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    } finally {
      setConverting(false);
    }
  };

  const handleSendWhatsapp = async () => {
    if (!selected || !whatsappMessage.trim()) return;
    setSendingWhatsapp(true);
    try {
      await adminApi.sendCrmWhatsapp(selected.entity_type, selected.id, {
        template_key: 'generic_crm_update',
        message: whatsappMessage.trim(),
        variables: {
          customer_name: selected.display_name || selected.name || 'Customer',
          message: whatsappMessage.trim(),
        },
      });
      toast.success('WhatsApp message queued/logged');
      setWhatsappMessage('');
      openDetail(selected);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send WhatsApp message');
    } finally {
      setSendingWhatsapp(false);
    }
  };

  return (
    <div className="p-8 space-y-6 bg-[#F8F6F3] min-h-screen">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">CRM</h1>
          <p className="text-[#A8A8A8] mt-1">Unified leads, customers, follow-ups, tasks, and WhatsApp actions.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.location.assign('/leads')}>Old Leads</Button>
          <Button variant="outline" onClick={() => window.location.assign('/tasks')}>Tasks</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-5 flex items-center gap-3"><Users className="text-[#C97A5E]" /><div><p className="text-sm text-[#A8A8A8]">Customers</p><p className="text-2xl font-bold text-[#2D2420]">{stats.customers}</p></div></CardContent></Card>
        <Card><CardContent className="p-5 flex items-center gap-3"><UserPlus className="text-blue-600" /><div><p className="text-sm text-[#A8A8A8]">Leads</p><p className="text-2xl font-bold text-[#2D2420]">{stats.leads}</p></div></CardContent></Card>
        <Card><CardContent className="p-5 flex items-center gap-3"><CalendarClock className="text-amber-600" /><div><p className="text-sm text-[#A8A8A8]">Due Follow-ups</p><p className="text-2xl font-bold text-[#2D2420]">{stats.due}</p></div></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-[#2D2420]">CRM Pipeline</CardTitle>
          <CardDescription>Search and act across leads and customers from one place.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_180px_auto] gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-[#A8A8A8]" />
              <Input className="pl-9" placeholder="Search name, shop, phone, tag..." value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && fetchRecords()} />
            </div>
            <Select value={filters.entity_type} onValueChange={(value) => setFilters((prev) => ({ ...prev, entity_type: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All CRM</SelectItem>
                <SelectItem value="lead">Leads</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={filters.follow_up_due ? 'default' : 'outline'} onClick={() => setFilters((prev) => ({ ...prev, follow_up_due: !prev.follow_up_due }))}>Follow-up Due</Button>
            <Button onClick={fetchRecords}>Search</Button>
          </div>

          {loading ? (
            <div className="py-10 text-center text-[#A8A8A8]">Loading CRM...</div>
          ) : records.length === 0 ? (
            <div className="py-10 text-center text-[#A8A8A8]">No CRM records found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Assigned</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Follow-up</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Tags</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {records.map((record) => (
                    <tr key={`${record.entity_type}-${record.id}`} className="hover:bg-[#FAFAF9]">
                      <td className="py-3 px-4">
                        <div className="font-semibold text-[#2D2420]">{record.display_name}</div>
                        <div className="text-xs text-[#A8A8A8]">{record.phone || record.email || '-'}</div>
                      </td>
                      <td className="py-3 px-4"><Badge className={cn(statusColor(record.status))}>{record.entity_type} · {record.status || record.stage || 'open'}</Badge></td>
                      <td className="py-3 px-4 text-[#5C4033]">{record.assigned_staff_name || '-'}</td>
                      <td className="py-3 px-4 text-[#5C4033]">{formatDate(record.follow_up_date)}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">{(record.tags || []).slice(0, 3).map((tag) => <Badge key={tag} variant="outline">{tag}</Badge>)}</div>
                      </td>
                      <td className="py-3 px-4 text-right"><Button size="sm" variant="outline" onClick={() => openDetail(record)}>Open</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">{selected?.display_name || 'CRM Detail'}</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-10 text-center text-[#A8A8A8]">Loading detail...</div>
          ) : selected ? (
            <Tabs defaultValue="overview">
              <TabsList><TabsTrigger value="overview">Overview</TabsTrigger><TabsTrigger value="tasks">Tasks</TabsTrigger><TabsTrigger value="whatsapp">WhatsApp</TabsTrigger></TabsList>
              <TabsContent value="overview" className="space-y-4 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card><CardContent className="p-4"><p className="text-xs text-[#A8A8A8]">Phone</p><p className="font-semibold text-[#2D2420]">{selected.phone || '-'}</p></CardContent></Card>
                  <Card><CardContent className="p-4"><p className="text-xs text-[#A8A8A8]">Assigned Staff</p><p className="font-semibold text-[#2D2420]">{selected.assigned_staff_name || '-'}</p></CardContent></Card>
                  <Card><CardContent className="p-4"><p className="text-xs text-[#A8A8A8]">Outstanding</p><p className="font-semibold text-[#2D2420]">{formatCurrency(selected.outstanding_amount)}</p></CardContent></Card>
                </div>
                <div className="flex flex-wrap gap-2">{(selected.tags || []).map((tag) => <Badge key={tag} variant="outline">{tag}</Badge>)}</div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-[#2D2420]">Timeline</h4>
                  {(selected.timeline || []).length === 0 ? <p className="text-sm text-[#A8A8A8]">No timeline entries yet.</p> : selected.timeline.map((item, index) => (
                    <div key={index} className="rounded-lg border border-[#E5E5E5] p-3 text-sm">
                      <p className="font-medium text-[#2D2420]">{item.action_type || item.type || 'Update'}</p>
                      <p className="text-[#5C4033]">{item.notes || item.message || item.description || '-'}</p>
                      <p className="text-xs text-[#A8A8A8] mt-1">{formatDate(item.created_at || item.timestamp)}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="tasks" className="pt-4 space-y-3">
                {(selected.tasks || []).length === 0 ? <p className="text-sm text-[#A8A8A8]">No linked tasks yet.</p> : selected.tasks.map((task) => (
                  <div key={task.id} className="rounded-lg border border-[#E5E5E5] p-3">
                    <div className="flex justify-between gap-3">
                      <p className="font-medium text-[#2D2420]">{task.title}</p>
                      <Badge className={statusColor(task.status)}>{task.status}</Badge>
                    </div>
                    <p className="text-sm text-[#5C4033]">{task.description}</p>
                    <p className="text-xs text-[#A8A8A8] mt-1">Due: {formatDate(task.due_date)}</p>
                  </div>
                ))}
              </TabsContent>
              <TabsContent value="whatsapp" className="pt-4 space-y-4">
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea rows={4} value={whatsappMessage} onChange={(e) => setWhatsappMessage(e.target.value)} placeholder="Type offer, pricing update, or follow-up note..." />
                  <Button onClick={handleSendWhatsapp} disabled={sendingWhatsapp || !whatsappMessage.trim()}><Send className="h-4 w-4 mr-2" />Send WhatsApp</Button>
                </div>
                {(selected.whatsapp_messages || []).map((log) => (
                  <div key={log.id} className="rounded-lg border border-[#E5E5E5] p-3 text-sm">
                    <div className="flex justify-between"><span className="font-medium text-[#2D2420]">{log.template_key}</span><Badge>{log.status}</Badge></div>
                    <p className="text-[#5C4033] mt-1">{log.message || '-'}</p>
                    <p className="text-xs text-[#A8A8A8] mt-1">{formatDate(log.created_at)}</p>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          ) : null}
          <DialogFooter className="gap-2">
            {selected?.entity_type === 'lead' && selected?.status !== 'converted' ? (
              <Button onClick={handleConvertLead} disabled={converting}><CheckCircle2 className="h-4 w-4 mr-2" />Convert to Customer</Button>
            ) : null}
            <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
